import {
  PRESCRIPTION_DEVIATION_CATEGORIES,
  type PrescriptionDeviationCategory,
  type PrescriptionEvidenceMatch,
  type PrescriptionEvidenceReview,
  type PrescriptionEvidenceReviewUpdate,
  type PrescriptionEvidenceStatus,
} from "@care-by-ella/contracts";
import type { StatusTone } from "./status-tone";

// TES-1057 / TES-1049 — the prescription document's review in the transfer
// review: the TES-1037 tone of each status, and the draft the control edits.
// Pure (no React), so `node --test` can run it.

export const EVIDENCE_STATUS_TONE: Record<
  PrescriptionEvidenceStatus,
  StatusTone
> = {
  not_reviewed: "neutral",
  verified: "complete",
  // Not usable as it stands: the basis is missing.
  insufficient: "missing",
  // Usable once she has completed it.
  completion_required: "incomplete",
};

export interface EvidenceDraft {
  status: PrescriptionEvidenceStatus;
  match: PrescriptionEvidenceMatch | null;
  categories: PrescriptionDeviationCategory[];
  otherSpecification: string;
}

export function draftOf(review: PrescriptionEvidenceReview): EvidenceDraft {
  return {
    status: review.status,
    match: review.matchesPatientData,
    categories: [...review.deviationCategories],
    otherSpecification: review.otherSpecification ?? "",
  };
}

export function toggleCategory(
  categories: readonly PrescriptionDeviationCategory[],
  category: PrescriptionDeviationCategory,
): PrescriptionDeviationCategory[] {
  const next = categories.includes(category)
    ? categories.filter((c) => c !== category)
    : [...categories, category];
  return PRESCRIPTION_DEVIATION_CATEGORIES.filter((c) => next.includes(c));
}

/**
 * What is still missing before the draft can be saved, in the words the
 * control shows; null when it can be saved. The API enforces the same rules.
 */
export function evidenceDraftProblem(draft: EvidenceDraft): string | null {
  if (draft.status === "not_reviewed" && draft.match !== null) {
    return "Välj en granskningsstatus innan du jämför med patientens uppgifter.";
  }
  if (draft.match === "deviates") {
    if (draft.categories.length === 0) return "Välj minst en kategori.";
    if (
      draft.categories.includes("other") &&
      draft.otherSpecification.trim() === ""
    ) {
      return "Skriv en kort specifikation för Annat.";
    }
  }
  return null;
}

/** The request body for a draft: categories only with a deviation. */
export function evidenceDraftPayload(
  draft: EvidenceDraft,
): PrescriptionEvidenceReviewUpdate {
  const deviates = draft.match === "deviates";
  const other = deviates && draft.categories.includes("other");
  return {
    status: draft.status,
    matchesPatientData: draft.match,
    deviationCategories: deviates ? draft.categories : [],
    otherSpecification: other ? draft.otherSpecification.trim() : null,
  };
}
